<script setup lang="ts">
  import { computed } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';

  export type TShellNoticeTone = 'warning' | 'danger' | 'info';

  /**
   * A full-width notice above the page (plan §4.2): plan expiry, email
   * verification. Tinted by tone; the page keeps its orange, so the action
   * is the dark button.
   */
  const props = withDefaults(
    defineProps<{
      tone: TShellNoticeTone;
      title: string;
      subtitle?: string;
      /** Extra explanation behind an info button (touch-friendly). */
      help?: string;
      actionText?: string;
      actionLoading?: boolean;
      dismissible?: boolean;
    }>(),
    {
      subtitle: undefined,
      help: undefined,
      actionText: undefined,
      actionLoading: false,
      dismissible: false,
    }
  );

  const emit = defineEmits<{
    (e: 'action'): void;
    (e: 'dismiss'): void;
  }>();

  const ICONS: Record<TShellNoticeTone, string> = {
    warning: 'alert-outline',
    danger: 'alert-circle-outline',
    info: 'information-outline',
  };

  const icon = computed(() => ICONS[props.tone]);
</script>

<template>
  <div
    class="v2-shell-notice hidden-print-only"
    :class="`tone-${tone}`"
    role="status"
  >
    <PosUiIcon :name="icon" class="notice-icon" />
    <div class="notice-text">
      <span class="notice-title">{{ title }}</span>
      <span v-if="subtitle" class="notice-subtitle">{{ subtitle }}</span>
      <!-- A tap (touch screens have no hover): the help, and the subtitle
           that narrow screens hide. -->
      <PosUiPopover
        v-if="help || subtitle"
        class="notice-more"
        :class="{ 'is-subtitle-only': !help }"
        placement="bottom"
        align="start"
      >
        <template #trigger="{ open, toggle }">
          <button
            type="button"
            class="notice-help"
            :aria-label="$t('shell.notice.more')"
            :aria-expanded="String(open)"
            @click="toggle"
          >
            <PosUiIcon name="information-outline" />
          </button>
        </template>
        <p v-if="subtitle" class="more-subtitle">{{ subtitle }}</p>
        <p v-if="help" class="more-help">{{ help }}</p>
      </PosUiPopover>
    </div>
    <PosUiButton
      v-if="actionText"
      class="notice-action"
      color="neutral"
      variant="solid"
      :loading="actionLoading"
      @click="emit('action')"
      >{{ actionText }}</PosUiButton
    >
    <button
      v-if="dismissible"
      type="button"
      class="notice-dismiss"
      :aria-label="$t('shell.notice.dismiss')"
      @click="emit('dismiss')"
    >
      <PosUiIcon name="close" />
    </button>
  </div>
</template>

<style scoped lang="scss">
  .v2-shell-notice {
    @apply tw-flex tw-min-h-12 tw-items-center tw-border-0 tw-border-b tw-border-solid tw-px-4 tw-py-1.5;
    // .pos-shell only fixes toolbars and headers: the notice keeps its size.
    flex: 0 0 auto;

    &.tone-warning {
      @apply tw-border-amber-200 tw-bg-amber-50;

      .notice-icon {
        @apply tw-text-amber-700;
      }
    }

    &.tone-danger {
      @apply tw-border-red-200 tw-bg-red-50;

      .notice-icon {
        @apply tw-text-red-700;
      }
    }

    &.tone-info {
      @apply tw-border-sky-200 tw-bg-sky-50;

      .notice-icon {
        @apply tw-text-sky-700;
      }
    }

    .notice-icon {
      @apply tw-mr-3 tw-shrink-0 tw-text-xl;
    }

    .notice-text {
      @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-wrap tw-items-center tw-text-sm;
    }

    .notice-title {
      @apply tw-mr-2 tw-font-semibold tw-text-neutral-900;
    }

    .notice-subtitle {
      @apply tw-mr-2 tw-text-neutral-600;

      @media (max-width: 959px) {
        display: none;
      }
    }

    // Only the subtitle to show: wide screens already show it inline.
    .notice-more.is-subtitle-only {
      @media (min-width: 960px) {
        display: none;
      }
    }

    .notice-help {
      // 32 px visual inside the 48 px row; the row's padding makes the hit
      // area reach 44 px.
      @apply tw-flex tw-h-8 tw-w-8 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-text-lg tw-text-neutral-500;

      &:hover {
        @apply tw-bg-neutral-900/5 tw-text-neutral-700;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }

    .more-subtitle,
    .more-help {
      @apply tw-m-0 tw-text-sm tw-leading-relaxed tw-text-neutral-700;
    }

    .more-subtitle {
      @media (min-width: 960px) {
        display: none;
      }

      & + .more-help {
        @apply tw-mt-2;
      }
    }

    .notice-action {
      @apply tw-ml-3 tw-shrink-0;
    }

    .notice-dismiss {
      @apply tw-ml-1 tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-xl tw-text-neutral-500;

      &:hover {
        @apply tw-bg-neutral-900/5 tw-text-neutral-700;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
  }
</style>
